import React, { Component, FormEvent } from 'react';
import { RootState } from '../../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import FormicaLabel from '../../custom/FormicaLabel';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import CustomLoading from '../../custom/CustomLoading';
import RoleHelper from '../../../RoleHelper';
import { ActionState } from '../../../redux/reducers/ActionReducer';
import { changeAction, changeActionIsUpdate, clearAction } from '../../../redux/actions/ActionActions';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { RemoveFromListActionJsonModel } from '../../../domain/collection/rule/action/ActionDomains';
import FormicaInput from '../../custom/FormicaInput';
import LanguageUtil from '../../../util/LanguageUtil';
import { List } from '../../../domain/list/ListDomains';
import eq = query.eq;
import ComponentName from '../../../constant/ComponentName';

class ActionRemoveFromListEditorComponent extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private listSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const {
      action: { action },
      component: { module },
      user,
    } = this.props;
    const { loading } = this.state;
    const isKyc = module.includes('kyc');
    const removeFromListAction = action as RemoveFromListActionJsonModel;

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'grid grid-cols-8 w-full !max-h-[calc(100vh-15rem)] overflow-y-scroll pb-8'}>
        <div className={'col-span-2 flex flex-col border-r border-f-black-5 px-5 py-5 gap-y-5'}>
          <div className={'flex flex-col gap-y-3'}>
            <span className={'text-primary font-bold text-md'}>
              {LanguageUtil.get({ id: 'pages.action.list.title', defaultMessage: 'INTEGRATION CONFIGURATION' })}
            </span>
            <span className={'flex bg-[#F7F8FA] rounded-[4px] w-full justify-between gap-x-1 px-1 py-1 h-[36px]'}>
              <div
                onClick={() => this.onSelectType('transaction')}
                className={`${action.type == 'transaction' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
              >
                {LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Transaction' })}
              </div>
              {isKyc && user.activeRiskType == 'compliance' && (
                <div
                  onClick={() => this.onSelectType('kyc')}
                  className={`${action.type == 'kyc' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
                >
                  {LanguageUtil.get({ id: 'common.label.kyc', defaultMessage: 'KYC' })}
                </div>
              )}
            </span>
            <div className={'flex flex-col w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })} />
              <FormicaSearchSelectBox
                options={this.getTransactionOptions()}
                rounded
                placeholder={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })}
                disabled={!RoleHelper.canEdit('Action')}
                value={action.transaction}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeTransaction(e)}
                data-testid={`${ComponentName.ACTION}_channel_select`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' })} />
              <FormicaSearchSelectBox
                options={this.getListOptions()}
                rounded
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.list', defaultMessage: 'List' })}
                disabled={!RoleHelper.canEdit('Action')}
                value={removeFromListAction.variables?.list}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeList(e)}
                data-testid={`${ComponentName.ACTION}_list_select`}
              />
            </div>
          </div>
        </div>
        <div className={'col-span-6 flex flex-col w-full h-full px-5 py-5 gap-y-2'}>
          <span className={'font-bold text-md text-primary'}>{LanguageUtil.get({ id: 'pages.action.list.list', defaultMessage: 'LIST INFORMATION' })}</span>
          <div className={'flex flex-col gap-y-1'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.item', defaultMessage: 'Item' })} />
            <FormicaInput
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.item', defaultMessage: 'Item' })}
              value={removeFromListAction.variables?.item}
              disabled={!RoleHelper.canEdit('Action')}
              onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeItem(e)}
              data-testid={`${ComponentName.ACTION}_list_field_item_input`}
            />
          </div>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const {
      action: { action },
    } = this.props;
    const removeFromListAction = action as RemoveFromListActionJsonModel;

    if (!removeFromListAction.variables) {
      removeFromListAction.variables = {
        list: '',
        item: '',
      };
    }

    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = async () => {
    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS);
    const listQuery = query.QueryBuilder.select().from(CollectionNames.LISTS).where(eq('type', 'INTERNAL_LIST')).select('id', 'name').build();

    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.listSubscription = subscribe.Formica.subscribe(CollectionNames.LISTS, listQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription, this.listSubscription]);
  };

  private tracker = async () => {
    const {
      action: { action },
    } = this.props;
    if (this.transactionSubscription?.ready() && this.listSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const listQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.LISTS).build();
      const transactions: TransactionJsonModel[] = await this.repository.findMany(transactionQuery);
      const lists: List[] = await this.repository.findMany(listQuery);
      const removeFromListAction = action as RemoveFromListActionJsonModel;

      if (removeFromListAction.variables?.list && this.state.selectedList == undefined) {
        const list = lists.find((list) => list.name === removeFromListAction.variables?.list);
        this.setState({ selectedList: list });
      }
      this.setState({ transactions, lists, loading: false });
    }
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    await this.listSubscription?.stop();
    this.computation?.stop();
  };

  private getTransactionOptions() {
    const {
      action: { action },
    } = this.props;
    if (action.type == 'kyc') {
      return [
        {
          label: LanguageUtil.get({ id: 'common.label.individual', defaultMessage: 'Individual' }),
          value: 'individual',
        },
        {
          label: LanguageUtil.get({ id: 'common.label.entity', defaultMessage: 'Entity' }),
          value: 'entity',
        },
      ];
    } else {
      return this.state.transactions.map((transaction) => {
        return {
          label: transaction.name,
          value: transaction.name,
        };
      });
    }
  }

  private getListOptions() {
    const { lists } = this.state;
    return lists.map((list) => {
      return {
        label: list.name,
        value: list.name,
      };
    });
  }

  private onChangeTransaction(e: FormicaSelectBoxItem) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.transaction = e.value;
    changeAction(action);
  }

  private onChangeList(e: FormicaSelectBoxItem) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    const removeFromListAction = action as RemoveFromListActionJsonModel;

    if (!removeFromListAction.variables) {
      removeFromListAction.variables = {
        list: '',
        item: '',
      };
    }

    removeFromListAction.variables.list = e.value;
    changeAction(removeFromListAction);

    const list = this.state.lists.find((list) => list.name === e.value);
    this.setState({ selectedList: list });
  }

  private onChangeItem(e: React.FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    const removeFromListAction = action as RemoveFromListActionJsonModel;

    if (!removeFromListAction.variables) {
      removeFromListAction.variables = {
        list: '',
        item: '',
      };
    }

    removeFromListAction.variables.item = e.currentTarget.value;
    changeAction(removeFromListAction);
  }

  private onSelectType(type: string) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.type = type;
    changeAction(action);
  }
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  transactions: TransactionJsonModel[] = [];
  lists: List[] = [];
  selectedList: List | undefined;
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  action: state.action as ActionState,
  isUpdate: state.action.isUpdate,
  lastCategory: state.page.lastCategory,
  component: state.component,
  user: state.auth.user,
});
const mapDispatchToProps = {
  changeActionIsUpdate,
  changeAction,
  clearAction,
  enterComponent,
  leaveComponent,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ActionRemoveFromListEditorComponent));
